import {AddressDisplayDTO, AddressDTO, Country} from "@/api/data-contracts";
import {CommonI18N} from "@/translations";

type AddressLike = AddressDTO | AddressDisplayDTO;

/**
 * Returns a localized country name for a known country code via the runtime i18n store.
 *
 * The lookup table is exhaustive over `Country` — a newly added enum value causes a
 * TypeScript error here instead of silently rendering an empty label. A missing
 * translation for the resolved key is NOT a fallback case for this function: `t(...)`
 * itself returns the technical key in plain text when the translation is absent
 * (`fallbackLng: false` in `frontend/src/i18n.ts`) — there is no German fallback on the
 * text level.
 *
 * @param {(key: string) => string} t - Translation function resolving a key to localized text.
 * @param {Country} country - The country code to format.
 * @returns {string} The localized country name, or an empty string for an unknown/missing country code.
 */
function formatCountry(t: (key: string) => string, country: Country): string {
    const countryKeyByCode: Record<Country, string> = {
        [Country.GERMANY]: CommonI18N.enum.country.GERMANY,
        [Country.NETHERLANDS]: CommonI18N.enum.country.NETHERLANDS,
        [Country.AUSTRIA]: CommonI18N.enum.country.AUSTRIA,
        [Country.SWITZERLAND]: CommonI18N.enum.country.SWITZERLAND,
        [Country.CZECHIA]: CommonI18N.enum.country.CZECHIA,
        [Country.POLAND]: CommonI18N.enum.country.POLAND,
        [Country.ITALY]: CommonI18N.enum.country.ITALY,
        [Country.FRANCE]: CommonI18N.enum.country.FRANCE
    };
    const key = countryKeyByCode[country];
    return key ? t(key) : "";
}

/**
 * Formats an address object into a single-line string based on the specified locale.
 *
 * @param {(key: string) => string} t - Translation function used to resolve the country name.
 * @param {AddressLike | undefined} address - The address object to format. If not provided, an empty string is returned.
 * @param {string} locale - The locale to use for formatting (address part order).
 * @returns {string} - The formatted address string with parts separated by commas.
 */
export function formatAddressToSingleLine(
    t: (key: string) => string,
    address: AddressLike | undefined,
    locale: string
): string {
    return formatAddress(t, address, locale).filter(Boolean).join(", ");
}

/**
 * Builds a list of address parts ordered for the specified locale.
 *
 * @param {(key: string) => string} t - Translation function used to resolve the country name.
 * @param {AddressLike | undefined} address - The address object to format.
 * @param {string} locale - The locale to use for formatting.
 * @returns {string[]} The list of address parts.
 */
export function formatAddress(
    t: (key: string) => string,
    address: AddressLike | undefined,
    locale: string
): string[] {
    if (!address) {
        return [];
    }
    const streetLine = [address.street ?? "", address.houseNumber ?? ""].filter(Boolean).join(" ");
    const hasPrimaryParts = [address.street, address.houseNumber, address.city, address.zip].some(
        (part) => (part ?? "").trim() !== ""
    );
    // Address part order stays a deliberate per-language-code branch (not a maintainable i18n
    // pattern/row key) — see ADR-012: a newly imported language uses the German order until the
    // next release.
    const countryLabel = hasPrimaryParts ? formatCountry(t, address.country) : "";
    return locale === "en"
        ? [streetLine, `${address.city ?? ""} ${address.zip ?? ""}`, countryLabel]
        : [streetLine, `${address.zip ?? ""} ${address.city ?? ""}`, countryLabel];
}

/**
 * Formats an address object into a multiline string based on the specified locale.
 *
 * @param {(key: string) => string} t - Translation function used to resolve the country name.
 * @param {AddressLike | undefined} address - The address object to format. If not provided, an empty string is returned.
 * @param {string} locale - The locale to use for formatting.
 * @returns {string} - The formatted address string with each part on a new line.
 */
export function formatAddressToMultiline(
    t: (key: string) => string,
    address: AddressLike | undefined,
    locale: string
): string {
    return formatAddress(t, address, locale).filter(Boolean).join("\n");
}
